import { useEffect } from "react";
import SessionProvider from "@/components/SessionProvider";
import { useAppLocation, navigate, RouteParams } from "./router";

// Pages — the exact components the Next app rendered, imported from the
// shared source tree. All client components; the shims supply their
// next/* imports.
import Launcher from "@/app/page";
import LoginPage from "@/app/login/page";
import TripsPage from "@/app/trips/page";
import TripChrome from "@/app/trip/[id]/TripChrome";
import HighlightsPage from "@/app/trip/[id]/page";
import ItineraryPage from "@/app/trip/[id]/itinerary/page";
import TransitPage from "@/app/trip/[id]/transit/page";
import HotelsPage from "@/app/trip/[id]/hotels/page";
import BudgetPage from "@/app/trip/[id]/budget/page";
import { FinanceDataProvider } from "@/lib/finance/finance-data-context";
import { FinanceChrome } from "@/components/finance/FinanceChrome";
import { FinanceShell } from "@/components/finance/FinanceShell";
import tripIndex from "@/lib/trips/data/index.json";

/** /trip → the most recently added trip known at build time (same rule the
 * Next redirect page applied). */
function TripRedirect() {
  useEffect(() => {
    navigate(`/trip/${(tripIndex as { id: string }[])[0].id}`, { replace: true });
  }, []);
  return null;
}

const TRIP_TABS: Record<string, () => React.JSX.Element> = {
  "": HighlightsPage,
  itinerary: ItineraryPage,
  transit: TransitPage,
  hotels: HotelsPage,
  budget: BudgetPage,
};

function TripShell({ id, tab }: { id: string; tab: string }) {
  const Tab = TRIP_TABS[tab] ?? HighlightsPage;
  return (
    <RouteParams params={{ id }}>
      <TripChrome>
        <Tab />
      </TripChrome>
    </RouteParams>
  );
}

function Routes() {
  const { pathname } = useAppLocation();
  const seg = pathname.split("/").filter(Boolean);

  if (seg.length === 0) return <Launcher />;
  if (seg[0] === "login") return <LoginPage />;
  if (seg[0] === "trips") return <TripsPage />;
  if (seg[0] === "trip") {
    if (!seg[1]) return <TripRedirect />;
    return <TripShell id={decodeURIComponent(seg[1])} tab={seg[2] ?? ""} />;
  }
  if (seg[0] === "finance") {
    return (
      <FinanceDataProvider>
        <FinanceChrome>
          <FinanceShell />
        </FinanceChrome>
      </FinanceDataProvider>
    );
  }
  return <Launcher />;
}

export default function App() {
  return (
    <SessionProvider>
      <Routes />
    </SessionProvider>
  );
}
